import { CONSTANTS } from '../../constants/constants';
import { encodeParam } from '../../services/params.utils';
import EllipsisTypography from '../EllipsisTypography/EllipsisTypography';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTheme } from '@mui/material/styles';
import NRAssetRiskChanged from '../NRAssetRiskChanged/NRAssetRiskChanged';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { removeTabAndSubTabFromFilePath } from '../../services/urlInterceptor.utils';
import { StatusFormatter } from '../NRMisconfigurationTable/NRMisconfigurationTableCustomComponents/NRMisconfigurationStatusProvider/NRMisconfigurationStatusProvider';
import { camelCaseToHuman, capitalizeFirstLetter, formatEPSSValue } from '../../utils/utils';

const { CREDENTIALS, LICENSE_ISSUES, SBOM, VULNERABILITIES, MISCONFIGURATIONS } = CONSTANTS.REPORT.TABS;
const { EQUAL } = CONSTANTS.FILTER_OPERATIONS;
const { EXPOSED_SECRETS } = CONSTANTS.REPORT.CREDENTIALS_SUB_TABS;
const { ACTIVITY_UPDATE_FIELD_LABELS, ACTIVITY_UPDATE_EXPLOIT_MATURITY_LABELS } = CONSTANTS.VULNERABILITIES;

function getVulnerabilityUpdateFieldLabel(field) {
  if (!field) return '';
  return (
    ACTIVITY_UPDATE_FIELD_LABELS[field] ??
    field
      .split('_')
      .map(part => capitalizeFirstLetter(part))
      .join(' ')
  );
}

function formatCvssScore(value) {
  const score = Number(value);
  return Number.isFinite(score) ? score.toFixed(1) : value ?? '';
}

function formatSeverityValue(value) {
  if (value == null || value === '') return '';
  const withoutPrefix = String(value).replace(/^SEVERITY_/i, '');
  return capitalizeFirstLetter(withoutPrefix.toLowerCase());
}

function formatExploitMaturityValue(value) {
  if (value == null || value === '') return '';
  const key = String(value).toLowerCase();
  return ACTIVITY_UPDATE_EXPLOIT_MATURITY_LABELS[key] ?? capitalizeFirstLetter(key);
}

function formatCisaKevValue(value) {
  if (value == null || value === '') return '';
  return capitalizeFirstLetter(String(value).toLowerCase());
}

function getVulnerabilityUpdateValue(field, value) {
  switch (field) {
    case 'epss_score':
      return formatEPSSValue(value);
    case 'cvss_score':
      return formatCvssScore(value);
    case 'severity':
      return formatSeverityValue(value);
    case 'exploit_maturity':
      return formatExploitMaturityValue(value);
    case 'cisa_kev_prevalence':
      return formatCisaKevValue(value);
    default:
      return value ?? '';
  }
}

function getFallbackHeaderLabel(item) {
  const activityTypeParts = item?.activityType?.split('_') ?? [];
  return activityTypeParts.slice(0, -1).map(capitalizeFirstLetter).join(' ');
}

function DefaultPrimaryHeader({ label, activity, linkProps, theme, endAdornment }) {
  return (
    <Box sx={{ display: 'flex', alignItems: 'center' }}>
      <EllipsisTypography
        {...(linkProps || {})}
        variant="body2"
        sx={{
          wordBreak: 'break-all',
          ...(linkProps ? { color: theme.palette.blue.link, cursor: 'pointer' } : {})
        }}
      >
        {label}
      </EllipsisTypography>
      <Typography sx={{ ml: 0.5, mr: 1 }} variant="body2" children={activity} />
      {endAdornment}
    </Box>
  );
}

function DescriptionSecondary({ item }) {
  if (!item?.description && !item?.user) return null;
  return (
    <EllipsisTypography variant="body2" sx={{ wordBreak: 'break-all' }} color="text.secondary">
      {item?.description}
      {!!item?.user ? ` by ${item?.user}` : ''}
    </EllipsisTypography>
  );
}

function StatusSecondary({ previous, current }) {
  return (
    <EllipsisTypography variant="body2" sx={{ wordBreak: 'break-all' }} color="text.secondary">
      {`Status: ${camelCaseToHuman(previous || '', { preserveAcronyms: false })} → ${camelCaseToHuman(current || '', {
        preserveAcronyms: false
      })}`}
    </EllipsisTypography>
  );
}

function HeaderBlock({ primary, secondary }) {
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', width: '100%' }}>
      {primary}
      {secondary}
    </Box>
  );
}

function NRActivityFeedItemHeader({ item, handleClose }) {
  const theme = useTheme();
  const { entity } = item;
  const location = useLocation();
  const activity = item?.activityType?.split('_').at(-1).toLowerCase();

  const navigate = useNavigate();

  const handleClick = () => {
    navigate(handleTableRedirect(entity, entity.__typename));
    handleClose();
  };

  const getEntityLabel = () => {
    return (
      entity?.cve ||
      entity?.checkName ||
      entity?.name ||
      entity?.componentName ||
      entity?.userName ||
      entity?.hash ||
      entity?.checkId ||
      entity?.groupId
    );
  };

  const handleLinkActivate = event => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      handleClick();
    }
  };

  const interactiveLinkProps = {
    role: 'link',
    tabIndex: 0,
    onClick: handleClick,
    onKeyDown: handleLinkActivate,
    'aria-label': `${getEntityLabel()} ${activity}`
  };

  const handleTableRedirect = (entity, typename) => {
    let redirectToBaseURL = removeTabAndSubTabFromFilePath(location);
    let extraParams = new URLSearchParams();
    let tab = '';
    let subTab = '';
    switch (typename) {
      case 'VulnerabilityUpdatedPayload':
      case 'VulnerabilityAddedPayload':
      case 'VulnerabilityRemediatedPayload':
        tab = VULNERABILITIES;
        extraParams.append(
          'vulnerabilityColumnFilter',
          encodeParam([
            {
              fieldName: 'CVE',
              value: entity.cve,
              operation: EQUAL
            }
          ])
        );
        break;
      case 'MisconfigChangedPayload':
        tab = MISCONFIGURATIONS;
        extraParams.append(
          'misconfigurationColumnsFilter',
          encodeParam([
            {
              fieldName: 'DISPLAYNAME',
              value: entity.checkName,
              operation: EQUAL
            }
          ])
        );
        break;
      case 'IdentificationAddedPayload':
        tab = SBOM;
        extraParams.append(
          'componentColumnFilter',
          encodeParam(
            [
              {
                fieldName: 'NAME',
                value: entity.name,
                operation: CONSTANTS.FILTER_OPERATIONS.EQUAL.toUpperCase()
              },
              {
                fieldName: 'VENDOR',
                value: entity.vendor,
                operation: CONSTANTS.FILTER_OPERATIONS.EQUAL.toUpperCase()
              },
              {
                fieldName: 'VERSION',
                value: entity.version,
                operation: CONSTANTS.FILTER_OPERATIONS.EQUAL.toUpperCase()
              }
            ].filter(item => item.value)
          )
        );
        break;
      case 'SecretRemediatedPayload':
        tab = CREDENTIALS;
        subTab = encodeParam(EXPOSED_SECRETS);
        extraParams.append('stringFilter', encodeParam(entity.filePath));
        break;
      case 'LicenseIssueRemediatedPayload':
        tab = LICENSE_ISSUES;
        extraParams.append(
          'licenseIssuesColumnFilter',
          encodeParam(
            [
              {
                fieldName: 'COMPONENTNAME',
                value: entity.componentName,
                operation: CONSTANTS.FILTER_OPERATIONS.EQUAL.toUpperCase()
              },
              {
                fieldName: 'ISSUENAME',
                value: entity.issueName,
                operation: CONSTANTS.FILTER_OPERATIONS.EQUAL.toUpperCase()
              }
            ].filter(item => item.value)
          )
        );
        break;
      default:
        break;
    }
    return `${redirectToBaseURL}${!!tab ? '/' + tab : ''}${!!subTab ? '/' + subTab : ''}?${extraParams.toString()}`;
  };

  switch (entity.__typename) {
    case 'VulnerabilityUpdatedPayload':
      return (
        <HeaderBlock
          primary={<DefaultPrimaryHeader label={entity.cve} activity={activity} linkProps={interactiveLinkProps} theme={theme} />}
          secondary={
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
              {entity.vulnerabilityUpdates?.map(update => (
                <Typography key={`${update.field}-${update.previous}-${update.current}`} sx={{ ml: 0.5 }} variant="body2" color="text.secondary">
                  {`${getVulnerabilityUpdateFieldLabel(update.field)}: ${getVulnerabilityUpdateValue(
                    update.field,
                    update.previous
                  )} → ${getVulnerabilityUpdateValue(update.field, update.current)}`}
                </Typography>
              ))}
            </Box>
          }
        />
      );
    case 'VulnerabilityRemediatedPayload':
      return (
        <HeaderBlock
          primary={<DefaultPrimaryHeader label={entity.cve} activity={activity} linkProps={interactiveLinkProps} theme={theme} />}
          secondary={<DescriptionSecondary item={item} />}
        />
      );
    case 'VulnerabilityAddedPayload':
      return (
        <HeaderBlock
          primary={<DefaultPrimaryHeader label={entity.cve} activity={activity} linkProps={interactiveLinkProps} theme={theme} />}
          secondary={<DescriptionSecondary item={item} />}
        />
      );
    case 'VulnerabilityRemovedPayload':
      return (
        <HeaderBlock
          primary={<DefaultPrimaryHeader label={entity?.cve} activity={activity} theme={theme} />}
          secondary={<DescriptionSecondary item={item} />}
        />
      );
    case 'MisconfigChangedPayload':
      return (
        <HeaderBlock
          primary={
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <EllipsisTypography
                {...interactiveLinkProps}
                variant="body2"
                sx={{ wordBreak: 'break-all', color: theme.palette.blue.link, cursor: 'pointer' }}
              >
                {entity.checkName}
              </EllipsisTypography>
              <Typography sx={{ mr: 1, whiteSpace: 'nowrap' }} variant="body2" children={` ${activity} to `} />
              <StatusFormatter value={entity?.newStatus} uppercase={entity?.newStatus?.toLowerCase() === 'na'} small={true} />
            </Box>
          }
          secondary={<DescriptionSecondary item={item} />}
        />
      );
    case 'IdentificationAddedPayload':
      return (
        <HeaderBlock
          primary={<DefaultPrimaryHeader label={entity.name} activity={activity} linkProps={interactiveLinkProps} theme={theme} />}
          secondary={<DescriptionSecondary item={item} />}
        />
      );
    case 'IdentificationRemovedPayload':
      return (
        <HeaderBlock
          primary={<DefaultPrimaryHeader label={entity?.name} activity={activity} theme={theme} />}
          secondary={<DescriptionSecondary item={item} />}
        />
      );
    case 'AssetGroupRemovedPayload':
      return (
        <HeaderBlock
          primary={<DefaultPrimaryHeader label={entity?.name} activity={activity} theme={theme} />}
          secondary={<DescriptionSecondary item={item} />}
        />
      );
    case 'AssetRiskChangedPayload':
      return (
        <HeaderBlock
          primary={
            <DefaultPrimaryHeader
              label={entity?.name}
              activity={activity}
              theme={theme}
              endAdornment={
                <NRAssetRiskChanged sx={{ ml: 'auto', display: 'flex', alignItems: 'center' }} oldRisk={entity.oldRisk} newRisk={entity.newRisk} />
              }
            />
          }
          secondary={<DescriptionSecondary item={item} />}
        />
      );
    case 'SecretRemediatedPayload':
      return (
        <HeaderBlock
          primary={
            <DefaultPrimaryHeader label={camelCaseToHuman(entity?.subtype)} activity="remediated" linkProps={interactiveLinkProps} theme={theme} />
          }
          secondary={<StatusSecondary previous={entity?.oldStatus} current={entity?.newStatus} />}
        />
      );
    case 'LicenseIssueRemediatedPayload':
      return (
        <HeaderBlock
          primary={
            <DefaultPrimaryHeader label={camelCaseToHuman(entity?.issueName)} activity="remediated" linkProps={interactiveLinkProps} theme={theme} />
          }
          secondary={<StatusSecondary previous={entity?.entityUpdates?.[0]?.previous} current={entity?.entityUpdates?.[0]?.current} />}
        />
      );
    default:
      return (
        <HeaderBlock
          primary={
            <DefaultPrimaryHeader
              label={
                entity?.name ||
                entity?.componentName ||
                entity?.userName ||
                entity?.hash ||
                entity?.checkId ||
                entity?.groupId ||
                getFallbackHeaderLabel(item)
              }
              activity={activity}
              theme={theme}
            />
          }
          secondary={<DescriptionSecondary item={item} />}
        />
      );
  }
}
export default NRActivityFeedItemHeader;
